import { useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import { useI18n } from '../i18n';
import './Modal.css';

interface ModalProps {
  open: boolean;
  title?: string;
  message: ReactNode;
  confirmText?: string;
  cancelText?: string | null;
  /** Opsiyonel 3. buton (cancel ve confirm arasında) — ör. "seç ama farklı aksiyon". */
  extraText?: string | null;
  onConfirm: () => void;
  onCancel?: () => void;
  onExtra?: () => void;
}

export function Modal({
  open,
  title,
  message,
  confirmText,
  cancelText = null,
  extraText = null,
  onConfirm,
  onCancel,
  onExtra,
}: ModalProps) {
  const { t } = useI18n();
  const resolvedConfirmText = confirmText ?? (t.common as any).ok;
  if (!open) return null;
  return (
    <div className="app-modal-overlay" onClick={() => (onCancel || onConfirm)()}>
      <div className="app-modal" onClick={e => e.stopPropagation()}>
        {title && <div className="app-modal-title">{title}</div>}
        <div className="app-modal-body">{message}</div>
        <div className="app-modal-actions">
          {cancelText && (
            <button className="app-modal-btn app-modal-btn-secondary" onClick={() => (onCancel || onConfirm)()}>
              {cancelText}
            </button>
          )}
          {extraText && (
            <button className="app-modal-btn app-modal-btn-secondary" onClick={() => (onExtra || onConfirm)()}>
              {extraText}
            </button>
          )}
          <button className="app-modal-btn app-modal-btn-primary" onClick={onConfirm} autoFocus>
            {resolvedConfirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

type ChoiceResult = 'confirm' | 'extra' | 'cancel';

interface ModalState {
  open: boolean;
  title?: string;
  message: ReactNode;
  confirmText?: string;
  cancelText?: string | null;
  extraText?: string | null;
  resolve?: (result: boolean) => void;
  resolveChoice?: (result: ChoiceResult) => void;
}

export function useAppModal() {
  const { t } = useI18n();
  const [state, setState] = useState<ModalState>({ open: false, message: '' });

  const close = useCallback((result: boolean) => {
    setState(prev => {
      prev.resolve?.(result);
      return { ...prev, open: false };
    });
  }, []);

  const closeChoice = useCallback((result: ChoiceResult) => {
    setState(prev => {
      prev.resolveChoice?.(result);
      return { ...prev, open: false };
    });
  }, []);

  const showAlert = useCallback(
    (message: ReactNode, title?: string, confirmText?: string) =>
      new Promise<void>(resolve => {
        setState({
          open: true,
          message,
          title,
          confirmText: confirmText ?? (t.common as any).ok,
          cancelText: null,
          resolve: () => resolve(),
        });
      }),
    [t],
  );

  const showConfirm = useCallback(
    (message: ReactNode, title?: string, confirmText?: string, cancelText?: string) =>
      new Promise<boolean>(resolve => {
        setState({
          open: true,
          message,
          title,
          confirmText: confirmText ?? t.common.yes,
          cancelText: cancelText ?? t.common.cancel,
          resolve,
        });
      }),
    [t],
  );

  /** 3 seçenekli dialog: 'confirm' | 'extra' | 'cancel' döner (dismiss = 'cancel'). */
  const showChoice = useCallback(
    (message: ReactNode, title: string | undefined, confirmText: string, extraText: string, cancelText?: string) =>
      new Promise<ChoiceResult>(resolve => {
        setState({
          open: true,
          message,
          title,
          confirmText,
          extraText,
          cancelText: cancelText ?? t.common.cancel,
          resolveChoice: resolve,
        });
      }),
    [t],
  );

  const modalElement = (
    <Modal
      open={state.open}
      title={state.title}
      message={state.message}
      confirmText={state.confirmText}
      cancelText={state.cancelText}
      extraText={state.extraText}
      onConfirm={() => (state.resolveChoice ? closeChoice('confirm') : close(true))}
      onCancel={() => (state.resolveChoice ? closeChoice('cancel') : close(false))}
      onExtra={() => closeChoice('extra')}
    />
  );

  return { showAlert, showConfirm, showChoice, modalElement };
}
